import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RotateCcw, Plus, X, Loader2, Tag, Eye } from "lucide-react";
import { Input } from "../ui/input";
import AdminOffers from "./AdminOffers";
import AdminExtras from "./AdminExtras";
import { Textarea } from "../ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "rounded-lg border-white/10 bg-[#0b0c0f] text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0";
const Label = ({ children, hint }) => (
  <label className="mb-2 flex items-baseline justify-between text-[12px] font-semibold uppercase tracking-[0.06em] text-white/60">
    <span>{children}</span>{hint && <span className="normal-case tracking-normal text-white/35">{hint}</span>}
  </label>
);

const ListEditor = ({ items, onChange, max = 8, testid, placeholder }) => (
  <div className="space-y-2" data-testid={testid}>
    {items.map((it, i) => (
      <div key={i} className="flex gap-2">
        <Input value={it} maxLength={120} placeholder={placeholder} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} className={`${field} h-10 flex-1`} data-testid={`${testid}-item-${i}`} />
        <button type="button" disabled={items.length <= 1} onClick={() => onChange(items.filter((_, j) => j !== i))} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-white/10 text-white/50 transition-colors hover:border-brand hover:text-brand disabled:opacity-30" aria-label="Remove line"><X className="h-4 w-4" /></button>
      </div>
    ))}
    {items.length < max && (
      <button type="button" onClick={() => onChange([...items, ""])} className="flex items-center gap-1.5 text-sm font-semibold text-white/60 hover:text-brand" data-testid={`${testid}-add`}><Plus className="h-4 w-4" />Add line</button>
    )}
  </div>
);

const PackageCard = ({ pkg, token, onSaved }) => {
  const [f, setF] = useState(pkg);
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  useEffect(() => setF(pkg), [pkg]);
  const dirty = JSON.stringify([f.tagline, f.intro, f.note, f.benefits, f.features]) !== JSON.stringify([pkg.tagline, pkg.intro, pkg.note, pkg.benefits, pkg.features]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/packages/${pkg.key}`, { tagline: f.tagline, intro: f.intro, note: f.note, benefits: f.benefits, features: f.features }, auth);
      toast.success(`${pkg.name} updated · live on the site`);
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please fill in the tagline, intro, and at least one benefit and bullet point.");
    } finally { setSaving(false); }
  };
  const reset = async () => {
    setSaving(true);
    try { await axios.post(`${API}/admin/packages/${pkg.key}/reset`, {}, auth); toast.success(`${pkg.name} reset to original text`); onSaved(); }
    catch { toast.error("Reset failed"); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#111217] p-6" data-testid={`package-editor-${pkg.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl font-bold uppercase text-white">{pkg.name}</h2>
          <div className="mt-1 flex items-center gap-1.5 text-sm text-white/50"><Tag className="h-3.5 w-3.5" />From £{pkg.price}{pkg.offer && <span className="text-brand"> ({pkg.offer.name}, was £{pkg.was_price})</span>} <span className="text-white/35">· change in Prices above</span></div>
        </div>
        {dirty && <span className="rounded-md border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-300">Unsaved changes</span>}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <div><Label hint={`${f.tagline.length}/60`}>Tagline (red label)</Label><Input value={f.tagline} maxLength={60} onChange={(e) => setF({ ...f, tagline: e.target.value })} className={`${field} h-11`} data-testid={`pkg-${pkg.key}-tagline`} /></div>
          <div><Label hint={`${f.intro.length}/800`}>Intro paragraph</Label><Textarea rows={6} maxLength={800} value={f.intro} onChange={(e) => setF({ ...f, intro: e.target.value })} className={`${field} h-auto`} data-testid={`pkg-${pkg.key}-intro`} /></div>
          <div><Label hint={`${f.note.length}/400`}>Small print</Label><Textarea rows={3} maxLength={400} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={`${field} h-auto`} data-testid={`pkg-${pkg.key}-note`} /></div>
        </div>
        <div className="space-y-5">
          <div><Label hint="shown in the pop-up">Key benefits</Label><ListEditor items={f.benefits} onChange={(v) => setF({ ...f, benefits: v })} testid={`pkg-${pkg.key}-benefits`} placeholder="e.g. Sharper throttle response" /></div>
          <div><Label hint="shown on the pricing card">Card bullet points</Label><ListEditor items={f.features} onChange={(v) => setF({ ...f, features: v })} testid={`pkg-${pkg.key}-features`} placeholder="e.g. Diagnostics scan" /></div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-white/10 pt-5">
        <button disabled={!dirty || saving} onClick={save} className="btn-red flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-50" data-testid={`pkg-${pkg.key}-save`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save changes</button>
        {dirty && <button onClick={() => setF(pkg)} className="rounded-lg border border-white/15 px-4 py-2.5 text-sm text-white/70 hover:border-white/40">Discard</button>}
        <a href="/#pricing" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2.5 text-sm text-white/70 hover:border-white/40"><Eye className="h-4 w-4" />View on site</a>
        <button onClick={() => setConfirmReset(true)} disabled={saving || !pkg.updated_at} className="ml-auto flex items-center gap-1.5 text-sm text-white/50 hover:text-brand disabled:opacity-30" data-testid={`pkg-${pkg.key}-reset`}><RotateCcw className="h-4 w-4" />Reset to original</button>
      </div>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent className="border-white/10 bg-[#111217] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Reset {pkg.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">Your edits will be replaced with the original package text.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white">Keep edits</AlertDialogCancel>
            <AlertDialogAction className="bg-brand text-white hover:bg-[#d0141c]" onClick={reset}>Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const PRICE_ROWS = [["stage1", "Stage 1"], ["stage2", "Stage 2"], ["custom", "Custom Tune"], ["deposit", "Mobile deposit"]];

const PricesCard = ({ token, onSaved }) => {
  const [saved, setSaved] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  useEffect(() => {
    axios.get(`${API}/admin/prices`, auth).then(({ data }) => { setSaved(data); setF(data); }).catch(() => toast.error("Couldn't load prices"));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const dirty = PRICE_ROWS.some(([k]) => Number(f[k]) !== Number(saved[k]));
  const valid = PRICE_ROWS.every(([k]) => Number(f[k]) >= 1);
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(PRICE_ROWS.map(([k]) => [k, Number(f[k])]));
      const { data } = await axios.put(`${API}/admin/prices`, body, auth);
      setSaved(data); setF(data);
      toast.success("Prices updated · site and online checkout now use the new prices");
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please enter valid prices (at least £1).");
    } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-brand/40 bg-[#111217] p-6" data-testid="prices-editor">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl font-bold uppercase text-white">Prices</h2>
          <p className="mt-1 text-sm text-white/50">Changes update the pricing cards, pay-in-3 amounts and online checkout instantly. Existing bookings keep the price they were booked at.</p>
        </div>
        {dirty && <span className="rounded-md border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-300">Unsaved changes</span>}
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PRICE_ROWS.map(([k, label]) => (
          <div key={k}>
            <Label>{label}</Label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/50">£</span>
              <Input type="number" min="1" step="0.01" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${field} h-11 pl-7`} data-testid={`price-input-${k}`} />
            </div>
            {k !== "deposit" && Number(f[k]) >= 1 && <div className="mt-1.5 text-[12px] text-white/40">Pay in 3: £{(Number(f[k]) / 3).toFixed(2)}</div>}
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-white/10 pt-5">
        <button disabled={!dirty || !valid || saving} onClick={save} className="btn-red flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="prices-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save prices</button>
        {dirty && <button onClick={() => setF(saved)} className="rounded-lg border border-white/15 px-4 py-2.5 text-sm text-white/70 hover:border-white/40" data-testid="prices-discard">Discard</button>}
      </div>
    </div>
  );
};

const AdminPackages = ({ token }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => axios.get(`${API}/packages`).then(({ data }) => setItems(data)).catch(() => toast.error("Couldn't load packages")), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-packages">
      <h1 className="font-display text-4xl font-bold text-white">Packages</h1>
      <p className="mt-1 text-white/55">Edit your prices and the text on your pricing cards and "View details" pop-ups. Saved changes go live straight away.</p>
      <div className="mt-8 space-y-6">
        <PricesCard token={token} onSaved={load} />
        <AdminOffers token={token} onChanged={load} />
        <AdminExtras token={token} />
        {items.map((p) => <PackageCard key={p.key} pkg={p} token={token} onSaved={load} />)}
      </div>
    </div>
  );
};

export default AdminPackages;
